import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveCustomer, updateCustomer, type Customer } from '@/services/customers';

interface CustomerModalProps {
  customer?: Customer | null;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = {
  firstName: '',
  lastName: '',
  documentId: '',
  phone: '',
  whatsapp: '',
  email: '',
  address: '',
  city: 'Bogotá',
  department: 'Cundinamarca',
  notes: '',
};

export default function CustomerModal({ customer, onClose, onSaved }: CustomerModalProps) {
  const isEditing = !!customer;
  const [form, setForm] = useState(() =>
    customer
      ? {
          firstName: customer.firstName,
          lastName: customer.lastName,
          documentId: customer.documentNumber,
          phone: customer.phone,
          whatsapp: customer.whatsapp,
          email: customer.email,
          address: customer.address,
          city: customer.city || 'Bogotá',
          department: customer.department || 'Cundinamarca',
          notes: customer.notes,
        }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof emptyForm>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Nombre y apellido son obligatorios.');
      return;
    }
    if (!form.phone.trim()) {
      setError('El teléfono es obligatorio.');
      return;
    }

    setSaving(true);
    const { error: saveError } = isEditing
      ? await updateCustomer(customer!.id, form)
      : await saveCustomer(form);
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
      subtitle={
        isEditing
          ? `Modificando: ${customer!.firstName} ${customer!.lastName}`
          : 'Agrega un cliente a la Patyfamilia.'
      }
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nombre *</label>
            <input className={inputCls} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="Ej. Laura" />
          </div>
          <div>
            <label className={labelCls}>Apellido *</label>
            <input className={inputCls} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Ej. Gómez" />
          </div>
          <div>
            <label className={labelCls}>Documento</label>
            <input className={inputCls} value={form.documentId} onChange={(e) => set('documentId', e.target.value)} placeholder="Ej. 1018452100" />
          </div>
          <div>
            <label className={labelCls}>Teléfono *</label>
            <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+57 3xx xxx xxxx" />
          </div>
          <div>
            <label className={labelCls}>WhatsApp</label>
            <input className={inputCls} value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder="+57 3xx xxx xxxx" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="correo@ejemplo.com" />
          </div>
          <div>
            <label className={labelCls}>Ciudad</label>
            <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Bogotá" />
          </div>
          <div>
            <label className={labelCls}>Departamento</label>
            <input className={inputCls} value={form.department} onChange={(e) => set('department', e.target.value)} placeholder="Cundinamarca" />
          </div>
        </div>

        <div>
          <label className={labelCls}>Dirección</label>
          <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Carrera 15 #120-45" />
        </div>

        <div>
          <label className={labelCls}>Notas</label>
          <textarea className={`${inputCls} resize-none`} rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Preferencias, talla habitual, etc." />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear cliente'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}